import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  RefreshCw,
  Check,
  AlertCircle,
  X,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Percent,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface Coupon {
  id: number;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_discount_cents: number | null;
  min_purchase_cents: number | null;
  usage_limit: number | null;
  current_uses: number;
  applicable_to: 'all' | 'subscription' | 'credits';
  expiry_date: string | null;
  status: 'active' | 'inactive';
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface CouponStats {
  total_coupons: number;
  active_coupons: number;
  total_uses: number;
  expired_coupons: number;
}

interface CouponUsage {
  id: number;
  user_email: string;
  user_name: string;
  original_amount_cents: number;
  discount_amount_cents: number;
  final_amount_cents: number;
  purchase_type: string;
  used_at: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export default function CouponManagement() {
  const { adminToken } = useAdminStore();

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [stats, setStats] = useState<CouponStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [couponUsage, setCouponUsage] = useState<CouponUsage[]>([]);

  // Form state
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 0,
    max_discount_cents: '',
    min_purchase_cents: '',
    usage_limit: '',
    applicable_to: 'all' as 'all' | 'subscription' | 'credits',
    expiry_date: '',
    status: 'active' as 'active' | 'inactive',
    description: '',
  });

  const fetchCoupons = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await fetch(`${API_URL}/admin/coupons?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data: ApiResponse<{ coupons: Coupon[]; stats: CouponStats }> = await response.json();
      if (data.success && data.data) {
        setCoupons(data.data.coupons);
        setStats(data.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
      setError('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  }, [adminToken, filterStatus]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleCreate = () => {
    setSelectedCoupon(null);
    setForm({
      code: '',
      discount_type: 'percentage',
      discount_value: 0,
      max_discount_cents: '',
      min_purchase_cents: '',
      usage_limit: '',
      applicable_to: 'all',
      expiry_date: '',
      status: 'active',
      description: '',
    });
    setShowModal(true);
  };

  const handleEdit = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setForm({
      code: coupon.code,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      max_discount_cents: coupon.max_discount_cents ? (coupon.max_discount_cents / 100).toString() : '',
      min_purchase_cents: coupon.min_purchase_cents ? (coupon.min_purchase_cents / 100).toString() : '',
      usage_limit: coupon.usage_limit?.toString() || '',
      applicable_to: coupon.applicable_to,
      expiry_date: coupon.expiry_date ? coupon.expiry_date.split('T')[0] : '',
      status: coupon.status,
      description: coupon.description || '',
    });
    setShowModal(true);
  };

  const handleView = async (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setCouponUsage([]);
    setShowViewModal(true);

    try {
      const response = await fetch(`${API_URL}/admin/coupons/${coupon.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data: ApiResponse<{ coupon: Coupon; usage_history: CouponUsage[] }> = await response.json();
      if (data.success && data.data) {
        setCouponUsage(data.data.usage_history || []);
      }
    } catch (err) {
      console.error('Failed to fetch coupon details:', err);
    }
  };

  const handleSave = async () => {
    if (!form.discount_value || form.discount_value <= 0) {
      setError('Valid discount value is required');
      return;
    }

    if (form.discount_type === 'percentage' && form.discount_value > 100) {
      setError('Percentage discount cannot exceed 100%');
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      code: form.code || undefined,
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      max_discount_cents: form.max_discount_cents ? Math.round(parseFloat(form.max_discount_cents) * 100) : null,
      min_purchase_cents: form.min_purchase_cents ? Math.round(parseFloat(form.min_purchase_cents) * 100) : null,
      usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
      applicable_to: form.applicable_to,
      expiry_date: form.expiry_date || null,
      status: form.status,
      description: form.description || null,
    };

    try {
      const url = selectedCoupon
        ? `${API_URL}/admin/coupons/${selectedCoupon.id}`
        : `${API_URL}/admin/coupons`;
      const method = selectedCoupon ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data: ApiResponse<{ code: string }> = await response.json();
      if (data.success) {
        setSuccess(selectedCoupon ? 'Coupon updated successfully!' : `Coupon created: ${data.data?.code || form.code}`);
        setShowModal(false);
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to save coupon');
      }
    } catch {
      setError('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/coupons/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSuccess('Coupon deleted successfully');
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to delete coupon');
      }
    } catch {
      setError('Failed to delete coupon');
    }
  };

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = coupon.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coupon.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getStatusColor = (coupon: Coupon) => {
    // Check if expired
    if (coupon.expiry_date && new Date(coupon.expiry_date) < new Date()) {
      return 'bg-red-100 text-red-600';
    }
    if (coupon.status === 'active') {
      return 'bg-green-100 text-green-600';
    }
    return 'bg-slate-100 text-slate-500';
  };

  const getStatusText = (coupon: Coupon) => {
    if (coupon.expiry_date && new Date(coupon.expiry_date) < new Date()) {
      return 'Expired';
    }
    return coupon.status;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Messages */}
      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-4 rounded-xl flex items-center gap-3 ${
              error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}
          >
            {error ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <Check className="w-5 h-5 text-green-500" />
            )}
            <span className={error ? 'text-red-600' : 'text-green-600'}>
              {error || success}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">Coupon Management</h1>
          <p className="text-sm sm:text-base text-slate-500">
            Create and manage discount coupons for your platform
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Coupon
        </button>
      </motion.div>

      {/* Stats */}
      {stats && (
        <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Coupons</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_coupons}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Coupons</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_coupons}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Uses</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_uses}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired_coupons}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search coupons..."
            className="w-full h-11 pl-12 pr-4 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'inactive'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filterStatus === status
                  ? 'bg-purple-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Coupons Table */}
      <motion.div variants={fadeInUp} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {filteredCoupons.length === 0 ? (
          <div className="p-12 text-center">
            <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Coupons Yet</h3>
            <p className="text-slate-500 mb-4">
              Create your first coupon to offer discounts
            </p>
            <button
              onClick={handleCreate}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow inline-flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Type & Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredCoupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono font-semibold text-slate-900">{coupon.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {coupon.discount_type === 'percentage' ? (
                          <Percent className="w-4 h-4 text-purple-500" />
                        ) : (
                          <DollarSign className="w-4 h-4 text-green-500" />
                        )}
                        <span className="text-slate-900">
                          {coupon.discount_type === 'percentage'
                            ? `${coupon.discount_value}%`
                            : `$${coupon.discount_value.toFixed(2)}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {coupon.current_uses}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ''}
                    </td>
                    <td className="px-6 py-4">
                      {coupon.expiry_date ? (
                        <span className={new Date(coupon.expiry_date) < new Date() ? 'text-red-500' : 'text-slate-600'}>
                          {formatDate(coupon.expiry_date)}
                        </span>
                      ) : (
                        <span className="text-slate-400">No expiry</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(coupon)}`}>
                        {getStatusText(coupon)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleView(coupon)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {selectedCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {selectedCoupon ? 'Update coupon details' : 'Set up a new discount coupon'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Code & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Coupon Code *
                    </label>
                    <input
                      type="text"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      placeholder="SAVE20"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 uppercase"
                    />
                    {!form.code && !selectedCoupon && (
                      <p className="text-xs text-slate-400 mt-1">Leave empty to auto-generate</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Status *
                    </label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'inactive' })}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Discount Type & Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Discount Type *
                    </label>
                    <select
                      value={form.discount_type}
                      onChange={(e) => setForm({ ...form, discount_type: e.target.value as 'percentage' | 'fixed' })}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      {form.discount_type === 'percentage' ? 'Percentage (0-100) *' : 'Amount ($) *'}
                    </label>
                    <input
                      type="number"
                      value={form.discount_value || ''}
                      onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })}
                      placeholder={form.discount_type === 'percentage' ? '10' : '5.00'}
                      min="0"
                      max={form.discount_type === 'percentage' ? 100 : undefined}
                      step={form.discount_type === 'percentage' ? 1 : 0.01}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                  </div>
                </div>

                {/* Max Discount (for percentage) */}
                {form.discount_type === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Maximum Discount ($, optional)
                    </label>
                    <input
                      type="number"
                      value={form.max_discount_cents}
                      onChange={(e) => setForm({ ...form, max_discount_cents: e.target.value })}
                      placeholder="50.00"
                      min="0"
                      step="0.01"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                    <p className="text-xs text-slate-400 mt-1">Leave empty for no cap</p>
                  </div>
                )}

                {/* Expiry & Usage Limit */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Expiry Date (optional)
                    </label>
                    <input
                      type="date"
                      value={form.expiry_date}
                      onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Usage Limit (optional)
                    </label>
                    <input
                      type="number"
                      value={form.usage_limit}
                      onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                      placeholder="100"
                      min="0"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                    <p className="text-xs text-slate-400 mt-1">Leave empty for unlimited</p>
                  </div>
                </div>

                {/* Applicable To & Min Purchase */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Applicable To
                    </label>
                    <select
                      value={form.applicable_to}
                      onChange={(e) => setForm({ ...form, applicable_to: e.target.value as 'all' | 'subscription' | 'credits' })}
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    >
                      <option value="all">All Payments</option>
                      <option value="subscription">Subscriptions Only</option>
                      <option value="credits">Credits Only</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Min Purchase ($, optional)
                    </label>
                    <input
                      type="number"
                      value={form.min_purchase_cents}
                      onChange={(e) => setForm({ ...form, min_purchase_cents: e.target.value })}
                      placeholder="10.00"
                      min="0"
                      step="0.01"
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Internal description for admin reference"
                    rows={2}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow disabled:opacity-50 flex items-center"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {selectedCoupon ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {showViewModal && selectedCoupon && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Coupon Details</h3>
                    <p className="text-sm font-mono text-purple-600">{selectedCoupon.code}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Coupon Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500 mb-1">Discount</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {selectedCoupon.discount_type === 'percentage'
                      ? `${selectedCoupon.discount_value}%`
                      : `$${selectedCoupon.discount_value.toFixed(2)}`}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500 mb-1">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(selectedCoupon)}`}>
                    {getStatusText(selectedCoupon)}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500 mb-1">Usage</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {selectedCoupon.current_uses}{selectedCoupon.usage_limit ? ` / ${selectedCoupon.usage_limit}` : ' (unlimited)'}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500 mb-1">Expiry</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {selectedCoupon.expiry_date ? formatDate(selectedCoupon.expiry_date) : 'No expiry'}
                  </p>
                </div>
              </div>

              {/* Usage History */}
              <div>
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Usage History</h4>
                {couponUsage.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No usage records yet</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {couponUsage.map((usage) => (
                      <div key={usage.id} className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{usage.user_name || usage.user_email}</p>
                          <p className="text-xs text-slate-500">{formatDate(usage.used_at)} - {usage.purchase_type}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-green-600">
                            -${(usage.discount_amount_cents / 100).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">
                            ${(usage.original_amount_cents / 100).toFixed(2)} to ${(usage.final_amount_cents / 100).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(selectedCoupon);
                  }}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Coupon
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
